import { TableCell, TableRow } from "@mui/material";
import Warning from "@mui/icons-material/Warning";

import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import BackOrderBadge from "@/main/components/shared/backOrderBadge/backOrderBadge";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ModalContactsWithIcon from "@/main/components/shared/modalContacts/modalContactsWithIcon";
import RenderTooltipMessage from "@/main/components/shared/renderTooltipMessage/renderTooltipMessage";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";

import IconChatSearchClaim from "@/main/components/shared/iconChatSearchClaim/iconChatSearchClaim";
import WrapperQuoteProcurementPartInfo from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import useKogControlElementId from "@/main/hooks/useKogControlElementId";
import { ClaimGridItemPartResponse, ClaimSearchGridResponse } from "@/main/models/response";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { Controls } from "@/main/services/constants/constantsControl";
import { convertStringToDecimal, getTranslateByKey, isEmpty, isNullUndefined } from "@/main/services/utils";
import { getStartandFinalDateByString } from "@/main/services/utilsDate";
import { formatCurrency } from "@/main/services/utilsNumber";

import * as S from "./styles";
import ThreeDotsPartSearchClaim from "./threeDotsPartSearchClaim";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";
import PartOriginAwareTooltip from "@/main/components/shared/partOriginInfo/partOriginAwareTooltip";
import * as StylesShared from "@/main/components/shared/styles";
import { userIsSupplier } from "@/main/services/utilsUser";

interface IGridCollapseSearchClaim {
	isLoadingDataParts: boolean;
	dataParts: ClaimGridItemPartResponse[] | null;
	dataGrid: ClaimSearchGridResponse;
	updateGridPartByGuidQuoteItem: (guidQuoteItem: string, objUpdate: Partial<ClaimGridItemPartResponse>) => void;
	refreshClaim: () => void;
}
function GridCollapseSearchClaim({
	dataParts,
	isLoadingDataParts,
	dataGrid,
	updateGridPartByGuidQuoteItem,
	refreshClaim,
}: IGridCollapseSearchClaim) {
	const ID_SCREEN = "id-screen-claim";
	const showPrices = useKogControlElementId(Controls.IdShowPrices);
	const showDepreciation = useKogControlElementId(Controls.IdShowDepreciation);

	const rowHeaders = [
		getTranslateByKey("information.part"),
		getTranslateByKey("information.supplier"),
		showPrices ? (
			<StylesShared.RowHeaderTextCenter key="price">
				{getTranslateByKey("information.price")}
			</StylesShared.RowHeaderTextCenter>
		) : (
			false
		),
		<StylesShared.RowHeaderTextCenter key="pickupDaysDeadline">
			{getTranslateByKey("information.pickupDaysDeadline")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="sent">
			{getTranslateByKey("procurementReportScheduleStatus.Sent")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="delivered">
			{getTranslateByKey("information.delivered")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="received">
			{getTranslateByKey("procurementstatus.received")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="shipping">
			{getTranslateByKey("information.shipping")}
		</StylesShared.RowHeaderTextCenter>,
		showDepreciation ? (
			<StylesShared.RowHeaderTextCenter key="depreciation">
				{getTranslateByKey("information.claimPartScreenDepreciation")}
			</StylesShared.RowHeaderTextCenter>
		) : (
			false
		),
		getTranslateByKey("information.status"),
		" ",
	];
	const sizeColumns = [
		"responsive", // Part
		"14%", // Supplier
		showPrices ? "100px" : false, // Price
		"120px", // Deadline
		"100px", // SentDate
		"100px", // Delivered
		"100px", // Received
		"10%", // Shipping
		showDepreciation ? "100px" : false, // Depreciation
		"80px", // Part Status
		"50px", // Icon
	];

	function goToSearchOrder(filter: string, date: string) {
		const { finalDate, startDate } = getStartandFinalDateByString(date);
		return `${RoutesConstants.orderSearchOrder}?quoteFilterVehicle=${filter}&startDate=${startDate}&finalDate=${finalDate}`;
	}
	const renderTooltipDateDate = (date: string, keyTranslation: string) =>
		isEmpty(date) ? "" : `${getTranslateByKey(keyTranslation)}: ${date}`;
	const renderValueOrDash = (date: string | undefined) => (isEmpty(String(date)) ? "-" : date);
	const isLaterCancelation = (part: ClaimGridItemPartResponse) =>
		part.cancelationPending && part.idProcurementStatus === ProcurementStatusEnum.Transit;
	const isCancelatedBySupplier = (part: ClaimGridItemPartResponse) =>
		part.canceledBySupplier && part.idProcurementStatus === ProcurementStatusEnum.Cancelled && !userIsSupplier();
	const isCancelationPendingInTransit = (part: ClaimGridItemPartResponse) =>
		part.cancelationPending && part.idProcurementStatus === ProcurementStatusEnum.Transit;

	function getStatusColor(part: ClaimGridItemPartResponse) {
		if (isLaterCancelation(part) || isCancelatedBySupplier(part)) {
			return "red";
		}

		return "";
	}

	function handleClickPart(part: ClaimGridItemPartResponse) {
		if (!isNullUndefined(part.procurementCode)) {
			const url = goToSearchOrder(String(part.procurementCode), String(part.procurementDate || ""));
			window.open(`#${url}`, "_blank");
			return;
		}
		if (!isNullUndefined(part.quoteCode)) {
			const url = goToSearchOrder(String(part.quoteCode), String(part.quoteDate || ""));
			window.open(`#${url}`, "_blank");
			return;
		}
	}
	function renderTooltipMessageStatus(part: ClaimGridItemPartResponse) {
		if (isCancelatedBySupplier(part)) {
			return getTranslateByKey("information.isCancelatedBySupplier");
		}

		if (isCancelationPendingInTransit(part)) {
			return getTranslateByKey("information.isOpenedCancelation");
		}

		if (isNullUndefined(part.reason) && isNullUndefined(part.reasonDescription) && !part.requoted) {
			if (part.idProcurementStatus === ProcurementStatusEnum.AwaitingApproval) {
				return getTranslateByKey("procurementStatus.awaitingApproval");
			}
			return `${part.procurementStatusName}`;
		}

		const reasonDescription = `${
			part.requoted && isEmpty(part.reason)
				? `${part.procurementStatusName} (${getTranslateByKey("reasonType.requoteProcurement")})`
				: !isNullUndefined(part.reasonDescription)
					? `${part.procurementStatusName} (${part.reasonType}${!isEmpty(part.reasonDescription) ? ` - ${part.reason} - ${part.reasonDescription})` : ""}`
					: part.reason
						? `${part.procurementStatusName} (${part.reasonType}${!isEmpty(part.reason) ? ` - ${part.reason})` : ""}`
						: ""
		}`;

		return `${reasonDescription}`;
	}

	if (isLoadingDataParts)
		return (
			<S.Container>
				<LoadingComponent />
			</S.Container>
		);
	if (isNullUndefined(dataParts))
		return (
			<S.Container>
				<ResultNotFoundComponent />
			</S.Container>
		);

	return (
		<S.Container>
			<BoxGrid
				marginCustom=".8rem 0 0"
				sizeCell="medium"
				rowHeaders={rowHeaders.filter(Boolean)}
				sizeColumns={sizeColumns.filter(Boolean) as string[]}
				breakpointTable={1300}
			>
				{dataParts.map((part, indexPart) => (
					<TableRow key={part.guidProcurementSupplierItem ?? indexPart}>
						<TableCell id="id-collumn-searchclaim-part">
							<S.CollumnPart>
								<PartOriginAwareTooltip
									interactive
									placement="right"
									PopperProps={{
										disablePortal: false,
									}}
									title={
										<S.AreaTooltipPart>
											<WrapperQuoteProcurementPartInfo
												data={part.quoteProcurementPartInfo}
												renderType="column"
												isEllipsisText={false}
												showLineSupplierInfo={{ info: "2lines" }}
												showOnlyPartType
											/>
											<div>
												<p>
													{getTranslateByKey("information.quotenr")}: {part.quoteCode}
												</p>
												<p>
													{getTranslateByKey("information.ordernr")}: {part.procurementCode}
												</p>
											</div>

											<p>{getTranslateByKey("information.clickOnPartRedirectClaimOrdersScreen")}</p>
										</S.AreaTooltipPart>
									}
								>
									<div style={{ minWidth: 0, overflow: "hidden", width: "fit-content" }}>
										<WrapperQuoteProcurementPartInfo
											isHyperLink
											onClick={() => handleClickPart(part)}
											data={part.quoteProcurementPartInfo}
											renderType="column"
											showLineSupplierInfo={{ info: "2lines" }}
											showIconNotes
											originPrice={part}
											showOnlyPartType
										/>
									</div>
								</PartOriginAwareTooltip>
							</S.CollumnPart>
						</TableCell>

						<TableCell id="id-collumn-searchclaim-supplier">
							<S.CollumnSupplier>
								<S.SupplierNameWrapper title={part.supplierName}>{part.supplierName}</S.SupplierNameWrapper>
								{!isEmpty(part.supplierName) && (
									<ModalContactsWithIcon
										idScreen="id-screen-claim"
										guidCompany={String(part.guidSupplierCompany)}
										infoProcurement={{
											claimNumber: String(dataGrid.claimNumber),
											insurerName: dataGrid.insurer,
											companyName: dataGrid.bodyshop,
											orderNumber: dataGrid.claimNumber,
											vehicleName: dataGrid.vehicle,
										}}
									/>
								)}
							</S.CollumnSupplier>
						</TableCell>

						{showPrices && (
							<TableCell id="id-collumn-searchclaim-price" align="center">
								{formatCurrency(part.price)}
							</TableCell>
						)}
						<TableCell id="id-collumn-searchclaim-deadline" align="center">
							<RenderTooltipMessage
								placement="top"
								tooltipMessage={renderTooltipDateDate(
									String(part.deliveryDateFormatted),
									"information.pickupDaysDeadline",
								)}
							>
								<S.CollumnDeadLine $hasDeadline={part.delayed}>
									{renderValueOrDash(part.deliveryDateFormatted)}
									{part.delayed && (
										<S.AreaIconDeadline>
											<Warning
												fontSize="small"
												style={{
													color: "#fab01c",
													margin: "-1px 2px 0 0",
												}}
											/>
										</S.AreaIconDeadline>
									)}
								</S.CollumnDeadLine>
							</RenderTooltipMessage>
							{part.backOrder && <BackOrderBadge />}
						</TableCell>

						<TableCell id="id-collumn-searchclaim-sent" align="center">
							<S.CollumnDeadLine $hasDeadline={false}>
								<RenderTooltipMessage
									placement="top"
									tooltipMessage={renderTooltipDateDate(
										String(part.sentDateFormatted),
										"procurementReportScheduleStatus.Sent",
									)}
								>
									{renderValueOrDash(part.sentDateFormatted)}
								</RenderTooltipMessage>
							</S.CollumnDeadLine>
						</TableCell>

						<TableCell id="id-collumn-searchclaim-delivered" align="center">
							<S.CollumnDeadLine $hasDeadline={false}>
								<RenderTooltipMessage
									placement="top"
									tooltipMessage={renderTooltipDateDate(String(part.deliveredDateFormatted), "information.delivered")}
								>
									{renderValueOrDash(part.deliveredDateFormatted)}
								</RenderTooltipMessage>
							</S.CollumnDeadLine>
						</TableCell>

						<TableCell id="id-collumn-searchclaim-received" align="center">
							<S.CollumnDeadLine $hasDeadline={false}>
								<RenderTooltipMessage
									placement="top"
									tooltipMessage={renderTooltipDateDate(
										String(part.receivedDateFormatted),
										"procurementstatus.received",
									)}
								>
									{renderValueOrDash(part.receivedDateFormatted)}
								</RenderTooltipMessage>
							</S.CollumnDeadLine>
						</TableCell>
						<TableCell id="id-collumn-searchclaim-shippingCompanyName" align="center">
							{isEmpty(part.shippingCompanyName) && isEmpty(part.trackingCode) ? (
								"-"
							) : (
								<RenderTooltipMessage
									placement="top"
									tooltipMessage={`${part.shippingCompanyName || ""}${part.shippingCompanyName && part.trackingCode ? " - " : ""}${part.trackingCode || ""}`}
								>
									<S.AreaShippingCompanyPart>
										<span>{part.shippingCompanyName}</span>
										<span>{part.trackingCode}</span>
									</S.AreaShippingCompanyPart>
								</RenderTooltipMessage>
							)}
						</TableCell>

						{showDepreciation && (
							<TableCell id="id-collumn-searchclaim-depreciation" align="center">
								{renderValueOrDash(part.depreciation ? `${convertStringToDecimal(String(part.depreciation))}%` : "")}
							</TableCell>
						)}

						<TableCell id="id-collumn-searchclaim-partStatus">
							<RenderTooltipMessage placement="top" tooltipMessage={renderTooltipMessageStatus(part)}>
								<S.CollumnStatus $color={getStatusColor(part)}>
									<S.TextPartStatus>
										{part.procurementStatusName}
										{part.requoted && isEmpty(part.reason)
											? ` (${getTranslateByKey("reasonType.requoteProcurement")})`
											: !isNullUndefined(part.reasonDescription)
												? ` (${part.reasonType}${!isEmpty(part.reasonDescription) ? ` - ${part.reason} - ${part.reasonDescription}` : ""})`
												: part.reason && ` (${part.reasonType}${!isEmpty(part.reason) ? ` - ${part.reason}` : ""})`}
									</S.TextPartStatus>
									{(isLaterCancelation(part) || isCancelatedBySupplier(part)) && (
										<Warning
											fontSize="small"
											style={{
												color: "#fab01c",
												margin: "-1px 2px 0 0",
											}}
										/>
									)}
								</S.CollumnStatus>
							</RenderTooltipMessage>
						</TableCell>
						<TableCell align="center" id="id-collumn-searchclaim-iconChat">
							<S.AreaIconRow>
								<ThreeDotsPartSearchClaim.Root idScreen={ID_SCREEN}>
									<ThreeDotsPartSearchClaim.OptionReassign
										externalPartId={part.externalPartId}
										claimNumber={String(dataGrid.claimNumber)}
										guidPart={String(part.guidPart)}
										guidProcurementSupplierItem={String(part.guidProcurementSupplierItem)}
										guidQuote={part.guidQuote}
										guidQuoteProcurement={String(part.guidProcurement)}
										idStatus={part.idProcurementStatus}
										cancelationPending={part.cancelationPending}
										isStripePayment={part.isStripePayment}
										showReassignCanceledBySupplier={part.showReassignCanceledBySupplier}
									/>
									<ThreeDotsPartSearchClaim.OptionEditPart
										guidCompany={String(part.guidSupplierCompany)}
										guidQuoteProcurement={String(part.guidProcurement)}
										guidProcurementSupplierItem={String(part.guidProcurementSupplierItem)}
										idStatus={part.idProcurementStatus}
										isStripePayment={part.isStripePayment}
										cancelationPending={part.cancelationPending}
										loadViewOrder={async () => {
											refreshClaim();
										}}
									/>
									<ThreeDotsPartSearchClaim.OptionCorrectPrice
										part={part}
										refreshClaim={() => {
											refreshClaim();
										}}
									/>
									<ThreeDotsPartSearchClaim.OptionImagesPart
										guidCompany={String(part.guidSupplierCompany)}
										guidProcurementSupplierItem={String(part.guidProcurementSupplierItem)}
										idStatus={part.idProcurementStatus}
										isStripePayment={part.isStripePayment}
									/>
									<ThreeDotsPartSearchClaim.OptionReopenPart
										guidProcurementSupplierItem={String(part.guidProcurementSupplierItem)}
										idStatus={part.idProcurementStatus}
										deliveryBySupplier={Boolean(part.deliveryBySupplier)}
										cancelationPending={part.cancelationPending}
										refreshClaim={() => {
											refreshClaim();
										}}
										visualizeRevertCancelation={Boolean(part.visualizeRevertCancelation)}
									/>
									<ThreeDotsPartSearchClaim.OptionReturn
										bodyshopResponseTime={part.bodyshopResponseTime}
										guidCompany={String(dataGrid.guidInsurerCompany)}
										idStatus={part.idProcurementStatus}
										isStripePayment={part.isStripePayment}
										deliveryBySupplier={Boolean(part.deliveryBySupplier)}
										guidProcurement={String(part.guidProcurement)}
										guidProcurementSupplier={String(part.guidProcurementSupplierItem)}
										guidProcurementSupplierItem={String(part.guidProcurementSupplierItem)}
										refreshGrid={() => {
											refreshClaim();
										}}
									/>
									<ThreeDotsPartSearchClaim.OptionCancel
										idStatus={part.idProcurementStatus}
										isStripePayment={part.isStripePayment}
										deliveryBySupplier={Boolean(part.deliveryBySupplier)}
										guidProcurement={String(part.guidProcurement)}
										guidProcurementSupplier={String(part.guidProcurementSupplier)}
										guidProcurementSupplierItem={String(part.guidProcurementSupplierItem)}
										cancelationPending={part.cancelationPending}
										refreshGrid={() => {
											refreshClaim();
										}}
									/>
								</ThreeDotsPartSearchClaim.Root>

								<IconChatSearchClaim
									guidQuoteItem={part.guidQuoteItem}
									lastComment={part.lastComment}
									quantity={part.commentsCount}
									updateGridPartByGuidQuoteItem={updateGridPartByGuidQuoteItem}
								/>
							</S.AreaIconRow>
						</TableCell>
					</TableRow>
				))}
			</BoxGrid>
		</S.Container>
	);
}

export default GridCollapseSearchClaim;
